/* eslint-disable react-hooks/exhaustive-deps */
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { filter, pipe } from "callbag-basics";
import subscribe from "callbag-subscribe";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import * as React from "react";
import { Editor, Element, Transforms } from "slate";
import { HistoryEditor } from "slate-history";
import { ReactEditor, useSlate } from "slate-react";

import { Button, InputField, Loader, ModalPortal } from "@livechat/design-system";
import { DesignToken } from "@livechat/design-system-react-components";

import { ButtonGroup } from "components/button/ButtonGroup";
import { AdaptedModal as Modal } from "components/design-system-adapters/AdaptedModal";
import { Icon, IconsEnum } from "components/icon";
import Text from "components/layout/Text";
// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line import/no-cycle
import { ExternalImageElement, ImageElement, LocalImageElement } from "components/rich-editor/types";
import { ResponsiveBreakpoint, ResponsiveBreakpointType } from "constants/responsive-breakpoint";
import { extractResourceIDsFromRichTextObj } from "helpers/text-editor";
import useModal from "hooks/use-modal";
import { AttachmentContext, useAttachments } from "routes/tickets/ticket/message-box/richtext/AttachmentProvider";
import { getItem, saveItem } from "services/session-storage";
import { actions$ } from "store/action-stream";
import { RequestAction } from "store/entities/actions";
import { TRANSACTION } from "store/entities/transaction/actions";
import {
  ITransactionUploadAttachmentFailurePayload,
  ITransactionUploadAttachmentSuccessPayload,
} from "store/entities/transaction/interfaces";
import { IAction, IActionWithPayload } from "store/helper";
import { withVar } from "themes/helpers";
import { uniqueId } from "utils/string";

import { EditorImageProps, isExternalImageElement, isLocalImageElement } from "../images";

const ImageContainer = styled.span`
  position: relative;
  width: auto;
  height: auto;
  display: inline-block;
  font-size: 0;

  img {
    max-width: 500px;
    height: auto;
  }

  @media (max-width: 500px) {
    img {
      max-width: 300px;
      height: auto;
    }
  }

  @media (min-width: ${ResponsiveBreakpoint[ResponsiveBreakpointType.Tablet]}px) {
    .alt-text-control {
      display: none;
    }

    &:hover {
      .alt-text-control {
        display: block;
      }
    }
  }
`;

const newImageClass = css`
  img {
    background-color: ${withVar(DesignToken.SurfacePrimaryDefault)};
    min-width: 200px;
    min-height: 200px;
  }
`;

const ImageContainerOverlay = styled.span`
  display: inline-block;

  width: 100%;
  height: 100%;

  position: absolute;
  top: 0px;
  left: 0px;

  a {
    color: ${withVar(DesignToken.ContentInvertPrimary)};
    text-decoration: none;
  }
`;

const EditorImageContainer: React.FC<{
  element: ImageElement;
  onImageLoad?: () => void;
  onImageError?: () => void;
  children?: (ref: React.Ref<HTMLElement>) => React.ReactNode | React.ReactNode;
}> = ({ element, onImageLoad, onImageError, children }) => {
  const overlayRef = useRef<HTMLDivElement | null>(null);
  const imageRef = useRef<HTMLImageElement | null>(null);
  const editor = useSlate();

  const src = useMemo(() => {
    if (isExternalImageElement(element)) {
      return element.url;
    }

    if (isLocalImageElement(element)) {
      return element.url || element.data || "";
    }

    return "";
  }, [element]);

  useEffect(() => {
    // FIXME: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    const handleLoad = () => {
      // @ts-expect-error TS(2345)
      const node = ReactEditor.toSlateNode(editor, imageRef.current);
      const path = ReactEditor.findPath(editor, node);
      // @ts-expect-error TS(2339): Property 'naturalWidth' does not exist on type 'HT... Remove this comment to see the full error message
      const { naturalWidth: width, naturalHeight: height } = imageRef.current;
      HistoryEditor.withoutSaving(editor as unknown as HistoryEditor, () => {
        Transforms.setNodes(
          editor,
          { width: String(width), height: String(height) },
          { at: path, match: Element.isElement, voids: true }
        );
      });

      if (onImageLoad) {
        onImageLoad();
      }
    };

    // FIXME: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    const handleError = () => {
      if (onImageError) {
        onImageError();
      }
    };

    if (imageRef.current !== null) {
      imageRef.current.addEventListener("load", handleLoad);
      imageRef.current.addEventListener("error", handleError);
    }

    // FIXME: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    return () => {
      if (imageRef.current) {
        imageRef.current.removeEventListener("load", handleLoad);
        imageRef.current.removeEventListener("error", handleError);
      }
    };
  }, [editor, onImageError, onImageLoad]);

  useEffect(() => {
    if (imageRef.current !== null) {
      // @ts-expect-error TS(2322): Type 'undefined' is not assignable to type 'string... Remove this comment to see the full error message
      imageRef.current.src = undefined;
      imageRef.current.src = src;
    }
  }, [onImageLoad, src]);

  return (
    <ImageContainer css={!element.width && !element.height && newImageClass}>
      <img alt={element.alt} width={element.width} height={element.height} ref={imageRef} className="editor-hd-image" />
      <ImageContainerOverlay ref={overlayRef}>
        {typeof children === "function" ? children(imageRef) : children}
      </ImageContainerOverlay>
    </ImageContainer>
  );
};

const LoaderPosition = styled.span`
  position: absolute;
  justify-self: flex-start;
  align-self: flex-start;
  padding: 8px;
`;

const ImageLoaderControl: React.FC = () => (
  <LoaderPosition>
    <Loader size="small" />
  </LoaderPosition>
);

const RemoveControl = styled.a`
  display: flex;
  width: 20px;
  height: 20px;
  align-items: center;
  justify-content: center;
  background-color: ${withVar(DesignToken.ContentBasicSecondary)};
  border-radius: 50%;
  text-align: center;
  color: ${withVar(DesignToken.ContentInvertPrimary)};

  justify-content: center;

  position: absolute;
  right: 5px;
  top: 5px;

  &:hover {
    background-color: ${withVar(DesignToken.ContentBasicPrimary)};
  }
`;

const RemoveIcon = styled(Icon)`
  align-self: center;
`;

const ImageUIRemoveControl: React.FC<{ element: EditorImageProps }> = ({ element }) => {
  const editor = useSlate();
  const linkRef = useRef(null);
  const [hidden, setHidden] = useState(false);

  const handleRemove = useCallback(
    // FIXME: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (event: any) => {
      let node;
      let path;
      if (linkRef.current) {
        node = ReactEditor.toSlateNode(editor, linkRef.current);
        path = ReactEditor.findPath(editor, node);
      }

      event.preventDefault();
      event.stopPropagation();
      if (node) {
        Transforms.unwrapNodes(editor, { at: path, match: Element.isElement, voids: true });
      }
    },
    [editor]
  );

  useEffect(() => {
    if ((element.width && parseInt(element.width, 10) < 40) || (element.height && parseInt(element.height, 10) < 60)) {
      setHidden(true);
    } else {
      setHidden(false);
    }
  }, [element.width, element.height]);

  return hidden ? null : (
    <RemoveControl href="" onClick={handleRemove} ref={linkRef}>
      <RemoveIcon name={IconsEnum.Close} width="10px" height="10px" fill={withVar(DesignToken.ContentInvertPrimary)} />
    </RemoveControl>
  );
};

const AltTextControl = styled.a`
  position: absolute;
  left: 8px;
  bottom: 8px;

  display: inline-block;
  background-color: ${withVar(DesignToken.ContentBasicSecondary)};
  padding: 4px 8px;
  line-height: 16px;
  border-radius: 6px;
  color: ${withVar(DesignToken.ContentInvertPrimary)};
  font-size: 13px;
  font-weight: 400;
  vertical-align: middle;
  text-decoration: none;

  &:active,
  &:hover,
  &:focus {
    background-color: ${withVar(DesignToken.ContentBasicPrimary)};
  }
`;

const AltTextIcon = styled(Icon)`
  padding-right: 3px;
  position: relative;
  top: 2px;
`;

const AltTextModalFooter: React.FC<{ onClose: () => void }> = ({ onClose }) => (
  <ButtonGroup>
    <Button size="large" onClick={onClose} secondary>
      Cancel
    </Button>
    <Button size="large" kind="primary" submit form="alt-text-form">
      Done
    </Button>
  </ButtonGroup>
);

const FullWidthInputField = styled(InputField)`
  margin: 1em 0;
  input {
    width: 100%;
  }
`;

const ModalContent = styled.div`
  max-width: 560px;
`;

const AltTextModal: React.FC<{
  onClose: () => void;
  linkRef: React.MutableRefObject<HTMLAnchorElement>;
  element: EditorImageProps;
}> = ({ onClose, linkRef, element }) => {
  const editor = useSlate();
  const [altText, setAltText] = useState(element.alt || "");
  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleAltTextChange = useCallback((event: any) => {
    setAltText(event.target.value);
  }, []);

  const handleSubmit = useCallback(
    // FIXME: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (event: any) => {
      event.preventDefault();
      event.stopPropagation();
      const node = ReactEditor.toSlateNode(editor, linkRef.current);
      const path = ReactEditor.findPath(editor, node);
      HistoryEditor.withoutSaving(editor as unknown as HistoryEditor, () => {
        Transforms.setNodes(editor, { alt: altText }, { match: Element.isElement, at: path, voids: true });
      });
      onClose();
    },
    [altText, editor, linkRef, onClose]
  );

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const stopFocusSteal = useCallback((event: any) => {
    event.stopPropagation();
  }, []);

  return (
    <Modal
      heading="Alt text"
      onClose={onClose}
      footer={<AltTextModalFooter onClose={onClose} />}
      onClick={stopFocusSteal}
    >
      <ModalContent>
        <Text fontSize="15px" lineHeight="22px">
          Alt text will be shown when the image cannot be displayed. It&lsquo;s also essential for people who use screen
          readers.
        </Text>
        <form onSubmit={handleSubmit} id="alt-text-form">
          <FullWidthInputField
            autoFocus
            labelText="Alt text"
            value={altText}
            onChange={handleAltTextChange}
            id="alt-text"
          />
        </form>
      </ModalContent>
    </Modal>
  );
};

const ImageAltTextControl: React.FC<{ element: EditorImageProps }> = ({ element }) => {
  const [altTextModal, altTextModalActions] = useModal(false);
  const [hidden, setHidden] = useState(false);

  const linkRef = useRef(null);
  const openModal = useCallback(
    // FIXME: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (event: any) => {
      event.preventDefault();
      altTextModalActions.show(true);
    },
    [altTextModalActions]
  );

  useEffect(() => {
    if ((element.width && parseInt(element.width, 10) < 80) || (element.height && parseInt(element.height, 10) < 30)) {
      setHidden(true);
    } else {
      setHidden(false);
    }
  }, [element.width, element.height]);

  return hidden ? null : (
    <>
      {/* @ts-expect-error TS(2559) */}
      <ModalPortal>
        {/* @ts-expect-error TS(2322): Type 'MutableRefObject<null>' is not assignable to... Remove this comment to see the full error message */}
        {altTextModal && <AltTextModal onClose={altTextModalActions.close} linkRef={linkRef} element={element} />}
      </ModalPortal>
      <AltTextControl className="alt-text-control" href="" onClick={openModal} ref={linkRef}>
        <AltTextIcon
          name={IconsEnum.Edit}
          inline
          width="12px"
          height="12px"
          fill={withVar(DesignToken.ContentInvertPrimary)}
        />{" "}
        Alt text
      </AltTextControl>
    </>
  );
};

const EditorExternalImage: React.FC<IComponentProps<ExternalImageElement>> = ({ element }) => {
  const [loaded, setLoaded] = useState(false);

  const handleLoaded = useCallback(() => {
    setLoaded(true);
  }, []);

  return (
    // @ts-expect-error TS(2746): This JSX tag's 'children' prop expects a single ch... Remove this comment to see the full error message
    <EditorImageContainer element={element} onImageLoad={handleLoaded}>
      {!loaded && <ImageLoaderControl />}
      <ImageUIRemoveControl element={element} />
      {loaded && <ImageAltTextControl element={element} />}
    </EditorImageContainer>
  );
};

const LoadingBar = styled.span`
  display: block;
  height: 4px;
  position: absolute;
  width: 100%;
  top: 0px;
  left: 0px;
`;

const LoadingBarProgress = styled.span`
  display: block;
  height: 100%;
  background-color: ${withVar(DesignToken.ActionPrimaryDefault)};
  transition: width 0.2s ease-in-out;
`;

const ImageUploadProgressBar: React.FC<{ uploadPercentage: number }> = ({ uploadPercentage }) => (
  <LoadingBar>
    <LoadingBarProgress style={{ width: `${uploadPercentage.toFixed(2)}%` }} />
  </LoadingBar>
);

class LocalImageDeleteGuard extends React.Component {
  static contextType = AttachmentContext;
  // @ts-expect-error TS(2612): Property 'context' will overwrite the base propert... Remove this comment to see the full error message
  context!: React.ContextType<typeof AttachmentContext>;

  // @ts-expect-error TS(2564): Property 'props' has no initializer and is not def... Remove this comment to see the full error message
  props: { element: LocalImageElement; localID: string; editor: Editor };

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  componentWillUnmount() {
    const { element, localID, editor } = this.props;
    const path = ReactEditor.findPath(editor as ReactEditor, element);
    try {
      // FIXME: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const [node] = Editor.node(editor, path) as any;
      if (node.type === "image" && node.fileName === element.fileName && node.resourceID === element.resourceID) {
        return;
      }
      if (element.resourceID && this.context.transactionID) {
        this.context.deleteAttachment(element.resourceID);
      } else {
        this.context.abortUpload(localID);
      }
      // FIXME: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (err) {
      if (element.resourceID && this.context.transactionID) {
        const otherInlineImageResourceIDs = extractResourceIDsFromRichTextObj(editor.children);

        const shouldRemoveFromTransaction =
          // @ts-expect-error TS(7006): Parameter 'id' implicitly has an 'any' type.
          otherInlineImageResourceIDs.filter(id => id === element.resourceID).length === 0;

        if (shouldRemoveFromTransaction) {
          this.context.deleteAttachment(element.resourceID);
        }
      } else {
        this.context.abortUpload(localID);
      }
    }
  }

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  render() {
    return null;
  }
}

const EditorLocalImage: React.FC<IComponentProps<LocalImageElement>> = ({ element }) => {
  const localID = useRef<LocalImageElement["localID"]>((element as LocalImageElement).localID || uniqueId());
  const imageRef = useRef(null);
  const editor = useSlate();
  const [uploadLock, setUploadLock] = useState(false);
  const [uploadPercentage, setUploadPercentage] = useState(0.0);
  const [loaded, setLoaded] = useState(false);
  const handleLoaded = useCallback(() => {
    setLoaded(true);
  }, []);

  const handleError = useCallback(() => {
    // @ts-expect-error TS(2345)
    const node = ReactEditor.toSlateNode(editor, imageRef.current);
    const path = ReactEditor.findPath(editor, node);

    Transforms.unwrapNodes(editor, { at: path, voids: true, match: Element.isElement });
  }, [editor]);

  const uploadProvider = useAttachments();

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const showProgressBar = (event: ProgressEvent) => {
    setUploadPercentage((event.loaded / event.total) * 100);
  };

  useEffect(() => {
    if (!element.resourceID && !element.localID) {
      const path = ReactEditor.findPath(editor, element);

      HistoryEditor.withoutSaving(editor as unknown as HistoryEditor, () => {
        Transforms.setNodes(editor, { localID: localID.current }, { at: path, match: Element.isElement, voids: true });
      });
    }
  }, [editor, element]);

  useEffect(() => {
    if (!element.resourceID && uploadProvider.enabled && !uploadLock && !element.localID) {
      uploadProvider.uploadAttachment(
        localID.current as string,
        element.data,
        showProgressBar,
        element.fileName as string,
        ["image/jpeg", "image/png", "image/gif", "image/webp", "image/bmp"]
      );
      setUploadLock(true);
    }
  }, [
    element.data,
    element.fileName,
    element.resourceID,
    showProgressBar,
    uploadLock,
    setUploadLock,
    uploadProvider,
    uploadProvider.enabled,
    element,
  ]);

  useEffect(() => {
    const unsubscribe = pipe(
      actions$,
      filter((action: IAction) => action.type === TRANSACTION.FETCH_SINGLE[RequestAction.FAILURE]),
      subscribe(() => {
        const path = ReactEditor.findPath(editor, element);
        Transforms.unwrapNodes(editor, { at: path, voids: true, match: Element.isElement });
      })
    );

    return unsubscribe;
  }, [editor, element, uploadProvider.transactionID]);

  useEffect(() => {
    const unsubscribe = pipe(
      actions$,
      // @ts-expect-error TS(2345)
      filter((action: IAction) =>
        [
          TRANSACTION.UPLOAD_ATTACHMENT[RequestAction.SUCCESS],
          TRANSACTION.UPLOAD_ATTACHMENT[RequestAction.FAILURE],
        ].includes(action.type)
      ),
      subscribe(
        (
          action:
            | IActionWithPayload<string, ITransactionUploadAttachmentSuccessPayload>
            | IActionWithPayload<string, ITransactionUploadAttachmentFailurePayload>
        ) => {
          const { localID: requestID } = action.payload;
          // @ts-expect-error TS(2345)
          const node = ReactEditor.toSlateNode(editor, imageRef.current);
          const path = ReactEditor.findPath(editor, node);

          if (requestID === localID.current) {
            if (action.type === TRANSACTION.UPLOAD_ATTACHMENT[RequestAction.SUCCESS]) {
              const {
                transactionID,
                attachment: { cid, url },
              } = action.payload as ITransactionUploadAttachmentSuccessPayload;
              const inlineImagesList = getItem(`${transactionID}_inlineImageAttachments`) || [];
              saveItem(`${transactionID}_inlineImageAttachments`, JSON.stringify([...inlineImagesList, cid]));

              HistoryEditor.withoutSaving(editor as unknown as HistoryEditor, () => {
                Transforms.setNodes(
                  editor,
                  { url, resourceID: cid, data: undefined, fileName: undefined, localID: undefined },
                  { at: path, match: Element.isElement, voids: true }
                );
              });
            } else if (action.type === TRANSACTION.UPLOAD_ATTACHMENT[RequestAction.FAILURE]) {
              Transforms.unwrapNodes(editor, { at: path, match: Element.isElement, voids: true });
            }
          }
        }
      )
    );

    return unsubscribe;
  }, [editor]);

  return (
    <EditorImageContainer element={element} onImageLoad={handleLoaded} onImageError={handleError}>
      {ref => {
        // @ts-expect-error TS(2531): Object is possibly 'null'.
        imageRef.current = ref.current;
        return (
          <>
            {uploadLock && !element.url && <ImageUploadProgressBar uploadPercentage={uploadPercentage} />}
            {!loaded && <ImageLoaderControl />}
            <ImageUIRemoveControl element={element} />
            {loaded && <ImageAltTextControl element={element} />}
            <LocalImageDeleteGuard localID={localID.current as string} element={element} editor={editor} />
          </>
        );
      }}
    </EditorImageContainer>
  );
};

interface IComponentProps<T = ImageElement> {
  element: T;
}

const EditorImage: React.FC<IComponentProps> = ({ element }) => {
  if (isExternalImageElement(element)) {
    return (
      <span contentEditable={false}>
        <EditorExternalImage element={element} />
      </span>
    );
  }

  if (isLocalImageElement(element)) {
    return (
      <span contentEditable={false}>
        <EditorLocalImage element={element} />
      </span>
    );
  }

  return null;
};

export default EditorImage;
